import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AssetUrls } from 'config/assets';
import './SendPageHeader.css';

interface MatchSummary {
  matchingCards: number;
  totalCards: number;
  matchingPackages: number;
  totalPackages: number;
}

const SORT_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'value-desc', label: 'Value: High to Low' },
  { value: 'value-asc', label: 'Value: Low to High' },
  { value: 'cards-desc', label: 'Most Cards' },
  { value: 'newest-desc', label: 'Newest Members' },
];

const ChevronSvg: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`send-sort-chevron ${open ? 'open' : ''}`}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path d="M3 4.5L6 7.5L9 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface Props {
  onSearch?: (query: string) => void;
  displayMode?: 'comfortable' | 'compact' | 'list';
  onDisplayModeChange?: (mode: 'comfortable' | 'compact' | 'list') => void;
  initialSearchValue?: string;
  // Auto-Match props
  globalAutoMatch?: boolean;
  onGlobalAutoMatchToggle?: (enabled: boolean) => void;
  matchSummary?: MatchSummary;
  isMatchIndexLoading?: boolean;
  hasMatchIndexError?: boolean;
  onRetryMatchIndex?: () => void;
  // Price filter props
  priceMin?: number;
  priceMax?: number;
  onPriceMinChange?: (value: number | undefined) => void;
  onPriceMaxChange?: (value: number | undefined) => void;
  // Sort props
  sortValue?: string;
  onSortChange?: (value: string) => void;
  // CSV Export props
  onExportCsv?: () => void;
  isExporting?: boolean;
  hasResults?: boolean;
}

export default function SendPageHeader({
  onSearch,
  displayMode = 'comfortable',
  onDisplayModeChange,
  initialSearchValue = '',
  globalAutoMatch = false,
  onGlobalAutoMatchToggle,
  matchSummary,
  isMatchIndexLoading = false,
  hasMatchIndexError = false,
  onRetryMatchIndex,
  priceMin,
  priceMax,
  onPriceMinChange,
  onPriceMaxChange,
  sortValue = 'default',
  onSortChange,
  onExportCsv,
  isExporting = false,
  hasResults = false,
}: Props) {
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setIsSortOpen(false);
      }
    };
    if (isSortOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSortOpen]);

  const currentSortLabel = SORT_OPTIONS.find(o => o.value === sortValue)?.label || 'Default';

  const handleSortSelect = useCallback((value: string) => {
    onSortChange?.(value);
    setIsSortOpen(false);
  }, [onSortChange]);

  return (
    <div className="send-header-container">
      {/* Header Text */}
      <div className="send-header-top">
        <div className="send-header-text">
          <h2 className="send-header-title">Available Trades</h2>
          <p className="send-header-subtitle">
            These are cards wanted by community members. Initiate a trade and send cards to earn points equivalent to their value.
          </p>
        </div>
      </div>

      {/* Search & Controls */}
      <div className="send-header-controls">
        <input
          type="text"
          className="send-search-input"
          placeholder="Search username, card name, or edition"
          value={initialSearchValue}
          onChange={(e) => onSearch?.(e.target.value)}
        />

        {/* Price Filter */}
        <div className="send-price-filter">
          <div className="send-price-field">
            <label>Price (min.)</label>
            <div className="send-price-input-wrapper">
              <img
                src={`${AssetUrls.base}/assets/icons/profile/coin.svg`}
                className="send-price-coin-icon"
                alt=""
              />
              <input
                type="text"
                className="send-price-input"
                placeholder="0"
                value={priceMin ?? ''}
                onChange={(e) => {
                  const val = e.target.value.replace(/,/g, '');
                  const num = parseInt(val, 10);
                  onPriceMinChange?.(isNaN(num) || num <= 0 ? undefined : num);
                }}
              />
            </div>
          </div>
          <div className="send-price-field">
            <label>Price (max.)</label>
            <div className="send-price-input-wrapper">
              <img
                src={`${AssetUrls.base}/assets/icons/profile/coin.svg`}
                className="send-price-coin-icon"
                alt=""
              />
              <input
                type="text"
                className="send-price-input"
                placeholder="999,999"
                value={priceMax ?? ''}
                onChange={(e) => {
                  const val = e.target.value.replace(/,/g, '');
                  const num = parseInt(val, 10);
                  onPriceMaxChange?.(isNaN(num) || num <= 0 ? undefined : num);
                }}
              />
            </div>
          </div>
        </div>

        {/* Global Auto-Match Toggle */}
        <div className={`send-auto-match-global ${hasMatchIndexError ? 'has-error' : ''}`}>
          <label className="send-auto-match-label">
            <span className="send-auto-match-icon">&#9889;</span>
            <span className="send-auto-match-text">Auto-Match</span>
            <div className="send-auto-match-toggle">
              <input
                type="checkbox"
                checked={globalAutoMatch}
                onChange={(e) => onGlobalAutoMatchToggle?.(e.target.checked)}
                disabled={isMatchIndexLoading}
              />
              <span className={`send-toggle-slider ${isMatchIndexLoading ? 'loading' : ''}`} />
            </div>
          </label>

          {/* Loading State */}
          {isMatchIndexLoading && (
            <span className="send-auto-match-loading">
              <span className="loading-spinner-small" />
              Loading...
            </span>
          )}

          {/* Error State */}
          {hasMatchIndexError && !isMatchIndexLoading && (
            <span className="send-auto-match-error">
              <span className="error-icon">!</span>
              <button
                className="send-auto-match-retry"
                onClick={onRetryMatchIndex}
              >
                Retry
              </button>
            </span>
          )}

          {/* Match Summary (when enabled and loaded) */}
          {globalAutoMatch && matchSummary && !isMatchIndexLoading && !hasMatchIndexError && (
            <span className="send-auto-match-summary">
              {matchSummary.matchingCards} cards in {matchSummary.matchingPackages} packages
            </span>
          )}
        </div>

        {/* Sort Dropdown */}
        <div className="send-sort-selector" ref={sortRef}>
          <button
            type="button"
            className={`send-sort-trigger ${isSortOpen ? 'open' : ''}`}
            onClick={() => setIsSortOpen(!isSortOpen)}
            aria-haspopup="listbox"
            aria-expanded={isSortOpen}
            aria-label="Select sort order"
          >
            <img src={AssetUrls.icons.controls.sort} alt="" className="send-sort-icon" />
            <span className="send-sort-text">{currentSortLabel}</span>
            <ChevronSvg open={isSortOpen} />
          </button>
          {isSortOpen && (
            <div className="send-sort-menu" role="listbox">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`send-sort-option ${sortValue === option.value ? 'selected' : ''}`}
                  onClick={() => handleSortSelect(option.value)}
                  role="option"
                  aria-selected={sortValue === option.value}
                >
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <select
          className="send-dropdown"
          value={displayMode}
          onChange={(e) => onDisplayModeChange?.(e.target.value as 'comfortable' | 'compact' | 'list')}
        >
          <option value="comfortable">Comfortable</option>
          <option value="compact">Compact</option>
          <option value="list">List</option>
        </select>

        {/* CSV Export Button */}
        {hasResults && onExportCsv && (
          <button
            className="send-export-button"
            onClick={onExportCsv}
            disabled={isExporting}
            title="Export filtered results as CSV"
            aria-label="Export to CSV"
          >
            {isExporting ? (
              <span className="loading-spinner-small" />
            ) : (
              <img
                src={`${AssetUrls.base}/assets/icons/download.svg`}
                className="send-export-icon"
                alt=""
              />
            )}
          </button>
        )}
      </div>
    </div>
  );
}
